import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useManifestReport } from '../hooks/use-manifest-report';
import { useManifest } from '../hooks/use-manifest';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useDialog } from '@/hooks/useDialog';
import { approveManifest, rejectManifest } from '../api/manifests-api';
import { ReportSummaryCards } from '../components/report-summary-cards';
import { FlaggedItemsTable } from '../components/flagged-items-table';
import { ScanActivityTimeline } from '../components/scan-activity-timeline';
import { AuditLogsTable } from '../components/audit-logs-table';
import { Dialog } from '@/components/ui/dialog';

export function ManifestReportPage() {
  const { id } = useParams<{ id: string }>();
  const manifestId = id ? parseInt(id, 10) : null;
  const { manifest, refetch: refreshManifest } = useManifest(manifestId);
  const { report, isLoading, error, refetch: refreshReport } = useManifestReport(manifestId);
  const { user } = useAuth();
  const { dialog, closeDialog, showConfirm, showError } = useDialog();
  const isAdmin = user?.role === 'Admin';

  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');

  const doApprove = async () => {
    if (!manifestId) return;
    setActionLoading(true);
    try {
      await approveManifest(manifestId);
      refreshManifest();
      refreshReport();
    } catch {
      showError('Error', 'Failed to approve manifest');
    } finally {
      setActionLoading(false);
    }
  };

  const doReject = async () => {
    if (!manifestId) return;
    setActionLoading(true);
    setShowRejectModal(false);
    try {
      await rejectManifest(manifestId, rejectNotes || undefined);
      setRejectNotes('');
      refreshManifest();
      refreshReport();
    } catch {
      showError('Error', 'Failed to reject manifest');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = () => {
    showConfirm(
      'Approve Manifest',
      'Approve this flagged manifest and mark it as Complete?',
      doApprove,
      'Approve'
    );
  };

  const handleReject = () => {
    setRejectNotes('');
    setShowRejectModal(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-700 rounded w-48 animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-gray-800 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="space-y-6">
        <Link to={`/manifests/${manifestId}`} className="text-blue-500 hover:text-blue-400">
          &larr; Back to Manifest
        </Link>
        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded">
          {error || 'Report not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to={`/manifests/${manifestId}`} className="text-blue-500 hover:text-blue-400">
          &larr; Back to Manifest
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Reconciliation Report</h1>
          {manifest && <p className="text-gray-400">{manifest.bargeName}</p>}
        </div>

        {isAdmin && manifest?.status === 'Flagged' && (
          <div className="flex gap-2">
            <button
              onClick={handleApprove}
              disabled={actionLoading}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded transition-colors font-semibold"
            >
              Approve
            </button>
            <button
              onClick={handleReject}
              disabled={actionLoading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded transition-colors font-semibold"
            >
              Reject
            </button>
          </div>
        )}
      </div>

      <ReportSummaryCards
        totalExpected={report.totalExpected}
        totalScanned={report.totalScanned}
        flaggedCount={report.flaggedCount}
        missingCount={report.missingCount}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FlaggedItemsTable items={report.flaggedItems} />
        <ScanActivityTimeline events={report.scanTimeline} />
      </div>

      <AuditLogsTable logs={report.auditLogs} />

      {/* Reject modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowRejectModal(false)} />
          <div className="relative bg-gray-800 rounded-lg border border-gray-700 shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-900 mb-4">
                <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white text-center mb-1">Reject Manifest</h3>
              <p className="text-sm text-gray-400 text-center mb-4">
                This will return the manifest to In Progress for re-scanning.
              </p>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Reason <span className="text-gray-500">(optional)</span>
              </label>
              <textarea
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-red-500 text-sm resize-none"
                placeholder="Enter a reason for rejection..."
                autoFocus
              />
            </div>
            <div className="bg-gray-750 px-6 py-4 flex gap-3 justify-end rounded-b-lg">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={doReject}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Reject Manifest
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog {...dialog} onClose={closeDialog} />
    </div>
  );
}
